/**
 * Account security (identity request): active sessions with revoke / sign out everywhere, and passkeys
 * (flag identity.passkeys: the section renders only when GET /v1/identity/passkeys answers; 404 = off).
 */
import { useState, type FormEvent } from 'react';
import type { PasskeyView } from '@qm/contracts/identity';
import { Badge, Button, DataTable, EmptyState, ErrorState, Field, Heading, Text, TextInput, type Column } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bffJson } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { isPasskeysOff, passkeyErrorKey, passkeysSupported, registerPasskey, removePasskey, renamePasskey } from '../../../lib/passkeys';
import { useResource } from '../../../lib/resource';
import { ListScreen, str } from '../_shared/ListScreen';

export function SecurityPage() {
  const { t, me, signOut } = useApp();
  const f = useFormat();
  const [done, setDone] = useState(false);
  const revoke = async (id: string) => { await bffJson(`/v1/identity/sessions/${encodeURIComponent(id)}/revoke`, 'POST').then(() => setDone(true), () => undefined); };
  const revokeAll = async () => { await bffJson('/v1/identity/sessions/revoke-all', 'POST', { includeCurrent: true }).catch(() => undefined); await signOut(); };
  return (
    <>
      <ListScreen titleKey="shell.screens.security.title" introKey="shell.screens.security.intro" path="/v1/identity/sessions" emptyKey="shell.screens.security.empty"
        actions={<Button onClick={() => void revokeAll()}>{t('shell.screens.security.signOutAll')}</Button>}
        columns={[
          { key: 'device', header: t('shell.col.device'), render: (r) => <bdi>{f.text(r.userAgent ?? r.device ?? r.host)}</bdi> },
          { key: 'createdAt', header: t('shell.col.created'), render: (r) => f.dateTime(str(r.createdAt)) },
          { key: 'lastSeenAt', header: t('shell.col.lastSeen'), render: (r) => f.dateTime(str(r.lastSeenAt)) },
          { key: 'actions', header: t('shell.col.action'), render: (r) => (r.id === me?.session.id
            ? <Badge tone="accent">{t('shell.screens.security.current')}</Badge>
            : <Button onClick={() => void revoke(r.id)}>{t('shell.screens.security.revoke')}</Button>) },
        ]}>
        {done ? <Text role="status">{t('shell.screens.security.revoked')}</Text> : null}
      </ListScreen>
      <PasskeysSection />
    </>
  );
}

/** Passkeys: list (name, created, last used), add, rename, remove. Hidden while passkeys are switched off. */
export function PasskeysSection() {
  const res = useResource<PasskeyView[]>('/v1/identity/passkeys');
  if (res.status === 'loading' || (res.status === 'error' && isPasskeysOff(res.error))) return null;
  return <PasskeysPanel rows={res.status === 'ready' ? res.data : null} failed={res.status === 'error'} reload={res.reload} supported={passkeysSupported()} />;
}

export function PasskeysPanel({ rows, failed, reload, supported }: { rows: PasskeyView[] | null; failed: boolean; reload: () => void; supported: boolean }) {
  const { t } = useApp();
  const f = useFormat();
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [codes, setCodes] = useState<string[]>([]);

  const act = async (run: () => Promise<unknown>, okKey: string) => {
    setError(null); setStatus(null); setCodes([]);
    try { await run(); setStatus(t(okKey)); setEditing(null); reload(); } catch (err) { setError(t(passkeyErrorKey(err))); }
  };
  // recoveryCodes come back once when this is the account's first recovery set (passkey-only accounts)
  const add = () => act(async () => { const r = await registerPasskey(t('shell.passkeys.defaultName')); if (r.recoveryCodes?.length) setCodes(r.recoveryCodes); }, 'shell.passkeys.added');
  const save = (e: FormEvent) => { e.preventDefault(); if (editing) void act(() => renamePasskey(editing.id, editing.name.trim()), 'shell.passkeys.renamed'); };

  const columns: Column<PasskeyView>[] = [
    { key: 'name', header: t('shell.col.name'), render: (r) => <><bdi>{r.name}</bdi>{r.deviceType === 'multiDevice' ? <> <Badge tone="neutral">{t('shell.passkeys.synced')}</Badge></> : null}</> },
    { key: 'createdAt', header: t('shell.col.created'), render: (r) => f.dateTime(r.createdAt) },
    { key: 'lastUsedAt', header: t('shell.passkeys.lastUsed'), render: (r) => (r.lastUsedAt ? f.dateTime(r.lastUsedAt) : t('shell.passkeys.never')) },
    { key: 'actions', header: t('shell.col.action'), render: (r) => (
      <>
        <Button onClick={() => setEditing({ id: r.id, name: r.name })}>{t('shell.passkeys.rename')}</Button>{' '}
        <Button onClick={() => void act(() => removePasskey(r.id), 'shell.passkeys.removed')}>{t('shell.passkeys.remove')}</Button>
      </>
    ) },
  ];

  return (
    <section className="qm-page" aria-labelledby="passkeys-title" data-testid="passkeys-section">
      <div className="qm-page__head">
        <div>
          <Heading level={2} id="passkeys-title">{t('shell.passkeys.title')}</Heading>
          <Text tone="muted">{t('shell.passkeys.intro')}</Text>
        </div>
        {supported ? <Button variant="primary" onClick={() => void add()} data-testid="passkey-add">{t('shell.passkeys.add')}</Button> : null}
      </div>
      {!supported ? <Text>{t('shell.passkeys.unsupported')}</Text> : null}
      {status ? <Text role="status">{status}</Text> : null}
      {codes.length ? (
        <div data-testid="passkey-recovery-codes">
          <Heading level={3}>{t('shell.signin.recoveryTitle')}</Heading>
          <Text>{t('shell.signin.recoveryIntro')}</Text>
          <ul className="qm-auth__codes" dir="ltr">{codes.map((rc) => <li key={rc}>{rc}</li>)}</ul>
        </div>
      ) : null}
      {error ? <ErrorState message={error} /> : null}
      {editing ? (
        <form onSubmit={save} noValidate>
          <Field label={t('shell.passkeys.name')}>
            {({ id, describedBy }) => <TextInput id={id} required maxLength={64} value={editing.name} aria-describedby={describedBy} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />}
          </Field>
          <Button variant="primary" type="submit" disabled={!editing.name.trim()}>{t('shell.passkeys.save')}</Button>{' '}
          <Button type="button" onClick={() => setEditing(null)}>{t('shell.passkeys.cancel')}</Button>
        </form>
      ) : null}
      {failed ? <ErrorState message={t('shell.error.load')} />
        : !rows?.length ? <EmptyState title={t('shell.passkeys.empty')} />
          : <DataTable caption={t('shell.passkeys.title')} columns={columns} rows={rows} />}
    </section>
  );
}
